import { logger } from "@/utils/logger";
import store from "@/store";
import { DownloadRequest, ElectronicSignKeys, FileResponse, MessageToSend, MessageToSendEnum, Signature_Doc_Type, Template } from "@/types";
import { deepClone } from "@/utils";
import mock from '@/store/services/document/mock/generatedFileMock.json'
import Notify from 'quasar/src/plugins/Notify.js';;
import i18n from "@/i18n";
import { settings } from "@/settings";
import { PDFDocument } from "pdf-lib";

import { useGeneralInfoStore } from "@/components/task/generalInfo/taskGeneralInfoStore";
import type { DocumentEmailSendOptions } from "@/store/services/task/actions/printDocumentService";

const getGeneralInfoStore = () => useGeneralInfoStore();
/**
 * Sends a message and handles the response by updating the task offer status.
 * 
 * @param {MessageToSend} messageToSend - The message to be sent.
 */
export const sentMessage = (messageTosend: any) => {
    store.dispatch("taskModule/printOfferModule/sendMessage", {
        messageTosend,
        callback: (res: any) => {
            const clone = deepClone(res);
            store.dispatch("taskModule/setTaskOfferStatus", { data: clone.data[0], context: "print" });
            if (messageTosend.messageName === MessageToSendEnum.SIGNATURE_PROCESS_MESSAGE) {
                Notify.create({
                    timeout: 10000,
                    actions: [{ icon: 'close', color: 'white' }],
                    message: i18n.global.t(`task.actions.${messageTosend.processVariables.signatureType.value}_${messageTosend.processVariables.currentAction.value}`),
                    color: 'positive'
                });
            }
           
            store.state.printDocumentModule!.isLoading = false;
        }
    });
};

/**
 * Retrieves recipient information from the provided data.
 * 
 * @param {any} data - The data containing recipient information.
 * @returns {any[]} - The list of recipients with role code 'CUSTOMER'.
 */
const getRecipient = (data: any) => {
    const customers: any = [];
    data.forEach((item: any) => {
        if (item.role_code === "CUSTOMER") {
            customers.push(item);
        }
    });
    return customers;
};

/**
 * Sends an electronic message.
 * 
 * @param {any} message - The message to be sent.
 */
const sendElectronicMessage = async (response: any, message: any) => {
    const transactionID = response.data.info.id;
    store.commit('printDocumentModule/setTransactionID', transactionID)
    let messageObj = deepClone(message);
    messageObj.processVariables.transactionId = {
        value: transactionID,
        type: "String"
    }
    sentMessage(messageObj);
};

function getEmailContact(recipient: any): any | null {
    let contacts;

    if (recipient.associatedParty.third.type?.id.includes("Organization")) {
        const signingCorrespondents = recipient.associatedParty.third.correspondents?.filter(
            (correspondent: any) => correspondent.signingAuthority === true
        ) || [];

        contacts = signingCorrespondents.flatMap(
            (correspondent: any) => correspondent.contactCorrespondent || []
        );
    } else {
        contacts = recipient.associatedParty.third.contacts || [];
    }

    const emailContact = contacts.find(
        (contact: any) => contact.contactMechanism?.type?.id === "EMAIL_CONTACT"
    );

    return emailContact || null;
}

function getPhoneContact(recipient: any): string | null {
    let contacts;

    if (recipient?.associatedParty?.third?.type?.id?.includes("Organization")) {
        const signingCorrespondents = recipient.associatedParty.third.correspondents?.filter(
            (correspondent: any) => correspondent.signingAuthority === true
        ) || [];

        contacts = signingCorrespondents.flatMap(
            (correspondent: any) => correspondent.contactCorrespondent || []
        );
    } else {
        contacts = recipient?.associatedParty?.third?.contacts || [];
    }

    const phoneContact = contacts.find(
        (contact: any) => contact.contactMechanism?.type?.id === "PHONE_CONTACT"
    );

    return phoneContact?.contactMechanism?.phoneNumber || null;
}


const buildDocRecipient = (recipients: any, signatureTag: string) => {
    let recipient: any = null;
    let emailContact = null;
    let phoneNumber = null;
    let email = null;
    let fullName = null;
    if (signatureTag === "$LOC$") {
        recipient = recipients.find((item: any) => item.associatedParty.role_code === "CUSTOMER")
    }
    else if (signatureTag.includes("$COLOC_")) {
        recipient = recipients.filter((item: any) => item.associatedParty.role_code === "COLOCATAIRE")[Number(signatureTag.split("_")[1].split('$')[0]) - 1]
    }

    if(recipient === undefined){
        recipient = recipients.find((item: any) => item.associatedParty.role_code === "CUSTOMER")
    }

    if (signatureTag !== "$SERVER_STAMP$" && recipient !== null) {
        phoneNumber = getPhoneContact(recipient);
        emailContact = getEmailContact(recipient);

        // if (!phoneContact || !phoneContact.contactMechanism.phoneNumber) {
        //     Notify.create({
        //         timeout: 20000,
        //         actions: [{ icon: 'close', color: 'white' }],
        //         color: "negative",
        //         message: i18n.global.t("main.missingRecipientPhoneNumber"),

        //     });
        //     throw new Error('Phone number is missing or empty.');

        // }

        if (!emailContact || !emailContact.contactMechanism.emailAddress) {
            Notify.create({
                actions: [{ icon: 'close', color: 'white' }],
                color: "negative",
                message: i18n.global.t("main.missingRecipientEmail"),
                timeout: 20000,

            });
            throw new Error('Email address is missing or empty.');
        }

        //
        //RCF-1176
        //
        if(recipient.associatedParty.third.type?.id.includes("Organization")){
           const signingCorrespondent =getSigningCorrespondent(recipient) 
            email = emailContact.contactMechanism.emailAddress;
            fullName = signingCorrespondent ?signingCorrespondent.name :recipient.associatedParty.third.commercialName
        }else{
            email = emailContact.contactMechanism.emailAddress;
            fullName = recipient.associatedParty.third.firstName + ' ' + recipient.associatedParty.third.familyName
        }

    }

    return {
        phoneNumber,
        email,
        fullName
    }
}

/**
 * Extracts signature information from metadata for a given recipient.
 * 
 * @param {any} response - The response containing metadata.
 * @param {any} recipient - The recipient for whom signatures are to be generated.
 * @returns {any[]} - The list of signature payloads or an error message if any required information is missing.
 */
export const getSignaturesFromMetaData = (response: any, recipients: any, originalDoc: any) => {
    let UniversignSignaturesPayload = []
    if (originalDoc.documentType !== Signature_Doc_Type.VISA) {
        const ElectronicSignPosition = response.metadata.filter((item: any) => item.key === ElectronicSignKeys.ELEC_SIGN_POSITION);
        UniversignSignaturesPayload = (ElectronicSignPosition as any[]).map((position: any) => {
                const value = JSON.parse(position.value);
                if (value.signatureTag === "$SERVER_STAMP$") {
                    const stampInfo = getStampInfoByFinancingProduct();
                    return {
                        page: value.page,
                        posX: value.x,
                        posY: value.y,
                        id: stampInfo.id,
                        name: stampInfo.name
                    }
                } else {
                    return {
                        page: value.page,
                        posX: value.x,
                        posY: value.y,
                        fullName: buildDocRecipient(recipients, value.signatureTag).fullName,
                        phoneNumber: buildDocRecipient(recipients, value.signatureTag).phoneNumber,
                        email: buildDocRecipient(recipients, value.signatureTag).email,
                        invitationSubject: ElectronicSignKeys.invitationSubject,
                        typeField: originalDoc.documentType || Signature_Doc_Type.SIGNATURE
                    }
                }
            
        }) ?? [];
    }

    else {
        UniversignSignaturesPayload.push({
            page: 1,
            posX: 1,
            posY: 1,
            fullName: buildDocRecipient(recipients, "$LOC$").fullName,
            phoneNumber: buildDocRecipient(recipients, "$LOC$").phoneNumber,
            email: buildDocRecipient(recipients, "$LOC$").email,
            invitationSubject: ElectronicSignKeys.invitationSubject,
            typeField: originalDoc.documentType
        });
        const hasColocataire = recipients.some(
            (item: any) => item.associatedParty.role_code === "COLOCATAIRE"
        );
       // const isIpidOrNotice = originalDoc.fileName?.toLowerCase().includes('ipid') || originalDoc.fileName?.toLowerCase().includes('notice');
        if (hasColocataire) {
            UniversignSignaturesPayload.push({
                page: 1,
                posX: 1,
                posY: 1,
                fullName: buildDocRecipient(recipients, "$COLOC_1$").fullName,
                email: buildDocRecipient(recipients, "$COLOC_1$").email,
                phoneNumber: buildDocRecipient(recipients, "$COLOC_1$").phoneNumber,
                invitationSubject: ElectronicSignKeys.invitationSubject,
                typeField: originalDoc.documentType
            });
        }
    }
    return UniversignSignaturesPayload;
};

const getStampInfoByFinancingProduct = () => {
  const financingProduct =
    store.state.demandModule?.offer?.proposals?.[0]?.proposalItems?.[0]?.financingProduct;

  if (!financingProduct) {
    return { id: '', name: '' };
  }
  if (financingProduct.includes('LOA') || financingProduct.includes('CBM')) {
    return {
      id: 'lpc_wZbdYJLwDX85',
      name: 'Capitole finance'
    };
  }

  if (financingProduct.includes('LLD')) {
    return {
      id: 'lpc_DMrkkxDn8Lev',
      name: 'Ecureuil service'
    };
  }

  return { id: '', name: '' };
};

const getIssuingEntityId = (): string => {
    const financingProduct =
        store.state.demandModule?.offer?.proposals?.[0]?.proposalItems?.[0]?.financingProduct;

    if (!financingProduct) return '';

    if (financingProduct.includes('LOA') || financingProduct.includes('CBM')) {
        return 'lpc_wZbdYJLwDX85'; 
    }

    if (financingProduct.includes('LLD')) {
        return 'lpc_DMrkkxDn8Lev'; 
    }

    return '';
};
interface Sealer {
    id: string;
    logoDisplay: string;
}

const getSealers = (): Sealer[] => {
    const financingProduct =
        store.state.demandModule?.offer?.proposals?.[0]?.proposalItems?.[0]?.financingProduct;

    if (!financingProduct) return [];

    if (financingProduct.includes('LOA') || financingProduct.includes('CBM')) {
        return [{ id: 'lpc_wZbdYJLwDX85', logoDisplay: 'certificate' }];
    }

    if (financingProduct.includes('LLD')) {
        return [{ id: 'lpc_DMrkkxDn8Lev', logoDisplay: 'certificate' }];
    }

    return [];
};
/**
 * Initializes the electronic signing process.
 * 
 * @param {any} currentRoute - The current route in the application.
 * @param {any} selectedTemplates - The selected templates for document generation.
 * @param {any} message - The message to be sent after generating the document.
 */
export const initElectronicSign = async (
    currentRoute: any,
    selectedTemplates: any,
    message: any,
    sendDoc?: boolean,
    mailSendOptions?: DocumentEmailSendOptions
) => {
    store.state.printDocumentModule!.isLoading = true;
    const fileList = store.getters['templateModule/getSelectedTemplates']
        .filter((template: string) => {
            const templates = store.getters['templateModule/getTemplates'];
            return templates.some((t: Template) => t.processId === template && t.signatureType === store.state.taskModule.selectedTask.variables.signatureMethod);
        }).sort((a:any, b:any) => {
            const templateA = store.getters['templateModule/getTemplates'].find((t:any) => t.processId === a);
            const templateB = store.getters['templateModule/getTemplates'].find((t:any) => t.processId === b);
            return (templateA?.documentOrder || 0) - (templateB?.documentOrder || 0);
        });;
   const selectedFilesToSign = store.getters['templateModule/getTemplates'].filter((template: Template) => fileList.includes(template.processId))
    downloadAllFiles(selectedFilesToSign.map((item: Template) => {
        return item.fileId
    }), selectedFilesToSign, message, sendDoc, mailSendOptions)

};


// /**
//  * Initiates the file download process and proceeds with the e-sign request.
//  *
//  * @param {any} message - The message to be sent after downloading the file.
//  */
// const downloadFile = (message: any) => {
//     store.dispatch("printDocumentModule/downloadFile", {
//         message: message,
//         callback: EsignRequest,
//     });
// };



/**
 * Prepares an e-sign request and initiates the e-signature process.
 * 
 * @param {any} response - The response containing document and metadata.
 * @param {any} message - The message to be sent.
 */
const EsignRequest = (responses: any, selectedFilesToSign: any, message: any) => {
    if (store.state.taskModule.offer) {

        let eSignRequest;
        let documentIds = [];
        let documents = [];
        let hasStamps = false;
        const { resourceUid, associatedParties } = store.state.taskModule.offer;
        const assignee = store.state.taskModule.selectedTask.assignee?.includes(' ## ') ?
            store.state.taskModule.selectedTask.assignee.substring(0, store.state.taskModule.selectedTask.assignee.indexOf(' ## ')) :
            store.state.taskModule.selectedTask.assignee;
        
        for (const response of responses) {
            const originalDoc = selectedFilesToSign.filter((item: any) => item.fileId === response.resourceUid)[0]
            const metaData: any = getSignaturesFromMetaData(response, associatedParties, originalDoc);
            const signatures: any = metaData.filter((item: any) => item.email) ?? []
            const stamps: any = metaData.filter((item: any) => item.id)
            if (stamps.length) {hasStamps = true;   }
            
            documentIds.push({
                "fileId": originalDoc.fileId,
                "fileName": originalDoc.fileName.trim(),
            })
            documents.push(
                {
                    "content": response.content,
                    "name": originalDoc.fileName.trim(),
                    "stamps": stamps,
                    "signatures": signatures
                }
            )
        }
        const offerReference = store.state.taskModule.offer?.reference || '';
        const offerExternalReference = store.state.taskModule.offer?.externalReference || '';
        const signatureRequest = {
            "documentIds": documentIds,
            "esignTransaction": {
                "autostart": true,
                "language": "fr",
                "duration": 15500,
                "senderName": assignee,
                "entity": "offer",
                "entityId": resourceUid,
                "folderId": "fol_Mx4ZqGwOAX84",
                //"issuingEntityId": getIssuingEntityId(),
                "documents": documents,
                "name": `Contrat ${offerReference} ${offerExternalReference}`,
                "sealers": hasStamps ? getSealers() : []
            },

        }
        store.dispatch("printDocumentModule/initEsign", {
            request: signatureRequest,
            message: message,
            callback: sendElectronicMessage,
        });
    }
};

function sendResponse() {
    store.state.printDocumentModule!.isLoading = false; 
}
async function downloadAllFiles(fileIds: string[], selectedFilesToSign: any, message: any, sendDoc?: boolean, mailSendOptions?: DocumentEmailSendOptions): Promise <void> {
    try {
        // Download all files in parallel
        const downloadPromises = fileIds.map(fileId =>
            downloadSingleFile({ resourceUid: fileId })
        );

        const responses = await Promise.all(downloadPromises);
        if (!sendDoc) {
            EsignRequest(responses, selectedFilesToSign, message)
        }
        else {
            store.dispatch("printDocumentModule/sendDocument", {
                mailSendOptions,
                responses: responses,
                selectedFilesToSign: selectedFilesToSign,
                callback: sendResponse,
            });
        }
        
       
    } catch(error) {
        logger.error('Error in downloadAndCombineFiles:', error);
        throw new Error('Failed to download and combine files');
    }
}

    /**
     * Downloads a single file
     * @param request Download request parameters
     * @returns Promise<FileResponse>
     */
async function downloadSingleFile(request: DownloadRequest): Promise < FileResponse > {
    return new Promise((resolve, reject) => {
        store.dispatch("printDocumentModule/downloadFileGeneratedFile", {
            request,
            callback: (response: FileResponse) => {
                if (response) {
                    resolve(response);
                } else {
                    reject(new Error('Download failed'));
                }
            },
        });
    });
}

function getSigningCorrespondent(offerAssociatedParty:any) {
  const third = offerAssociatedParty?.associatedParty?.third;

  // correspondants au niveau third ou organization
  const correspondents =
    third?.correspondents ??
    third?.organization?.correspondents ??
    [];

  const correspondent = correspondents.find((c:any) => c.signingAuthority === true);

  if (!correspondent) {
    return null;
  }

  const name = [correspondent.firstName, correspondent.middleName, correspondent.familyName]
    .filter((part) => part != null && String(part).trim() !== '')
    .join(' ')
    .trim();

  const email =
    correspondent.businessData?.email ??
    getEmailFromContactCorrespondent(correspondent.contactCorrespondent);

  return { name, email };
}

function getEmailFromContactCorrespondent(contactCorrespondents = []) {
  const emailContact:any = contactCorrespondents.find(
    (cc:any) =>
      cc?.contactMechanism?.objectType?.includes('emailcontact') &&
      cc.contactMechanism.emailAddress
  );
  return emailContact?.contactMechanism?.emailAddress ?? null;
}